import { ResourcePages, ResourcePagesType } from '@backrs_ui/common-ui';

import { AppIcon } from '@backrs_ui/common-ui/src/designSystem/icons/AppIcon';
import { appStrings } from '@backrs_ui/grow-ui/src/assets/appStrings';

import { Box, SxProps, Theme, Avatar, Chip } from '@mui/material';

type MakeItPersonalHeaderProps = Readonly<{
  pageName: ResourcePagesType;
  profilePicture?: string;
}>;

const headerImageMapping: Record<ResourcePagesType, string> = {
  [ResourcePages.COLLEGE]: '/images/make-it-personal-college.svg',
  [ResourcePages.LIFE_READINESS]: '/images/make-it-personal-life-readiness.svg',
  [ResourcePages.EMPLOYER]: '/images/make-it-personal-employer.svg',
};

const MAKE_IT_PERSONAL_STRINGS = appStrings.modules.makeItPersonal;

export const MakeItPersonalHeader = ({ pageName, profilePicture }: MakeItPersonalHeaderProps) => {
  return (
    <Box sx={headerContainerSx}>
      <Box sx={imageContainer}>
        <Box component="img" src={headerImageMapping[pageName]} sx={makeItPersonalImage} />
        <Box sx={forYouSx}>
          <Chip
            variant="badge"
            label={MAKE_IT_PERSONAL_STRINGS.forYou}
            size="md"
            icon={
              profilePicture ? (
                <Avatar sx={profileAvatarStyle} src={profilePicture} alt="Profile Picture" />
              ) : (
                <Box sx={defaultAvatarSx}>
                  <AppIcon
                    iconName="User01"
                    diameter="12px"
                    color={'Colors/Foreground/fg-quaternary'}
                  />
                </Box>
              )
            }
            sx={chipSx}
            type="leading"
          />
        </Box>
      </Box>
    </Box>
  );
};

const headerContainerSx: SxProps<Theme> = ({ backrs }) =>
  ({
    backgroundColor: backrs.colors['Beige/100'],
    width: '100%',
    display: 'flex',
    justifyContent: 'center',
    borderBottom: `1px solid ${backrs.colors['Colors/Base/Black']}`,
  }) as const;

const imageContainer: SxProps<Theme> = {
  position: 'relative',
  width: {
    xs: '100%',
    sm: '50%',
    md: '100%',
  },
  alignSelf: 'center',
} as const;

const makeItPersonalImage: SxProps<Theme> = ({ backrs }) =>
  ({
    width: '100%',
    alignSelf: 'center',
    paddingBottom: backrs.spacing['spacing-md'],
  }) as const;

const chipSx: SxProps<Theme> = ({ backrs }) => ({
  backgroundColor: backrs.colors['Colors/Base/White'],
  '& .MuiChip-icon': {
    marginLeft: backrs.spacing['spacing-none'],
    marginRight: backrs.spacing['spacing-none'],
  },
});

const profileAvatarStyle: SxProps<Theme> = ({ backrs }) =>
  ({
    width: '16px',
    height: '16px',
    border: `1px solid ${backrs.colors['Colors/Base/Black']}`,
  }) as const;

const forYouSx: SxProps<Theme> = {
  position: 'absolute',
  top: '12%',
  right: '6%',
};

const defaultAvatarSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  borderRadius: backrs.radius['radius-full'],
  padding: backrs.spacing['spacing-xxs'],
  border: `1px solid ${backrs.colors['Colors/Base/Black']}`,
});
